import {
    useEffect,
    useRef,
    useState,
    Fragment,
    Dispatch,
    SetStateAction
} from 'react';
import styled, { css } from 'styled-components';
import {
    HiChevronDoubleLeft,
    HiChevronDoubleRight,
    HiChevronLeft,
    HiChevronRight
} from 'react-icons/hi2';
import { darken } from 'polished';

import theme from '@styles/theme';
import StackIcon from '@assets/icon/stackIcon.svg';
import ArrowDown from '@assets/icon/paginationArrowDown.svg';

type PaginationProps = {
    pageGroupLength: number;
    pageIndex: number;
    take: number;
    takeOptions?: number[];
    totalLength: number;
    setTake: Dispatch<SetStateAction<number>>;
    setPageIndex: Dispatch<SetStateAction<number>>;
    prevPageHandler: () => void;
    nextPageHandler: () => void;
    prevPageGroupHandler: () => void;
    nextPageGroupHandler: () => void;
    resetPaginationHandler: () => void;
    disabled: Record<string, boolean>;
    padding?: string;
};

const Pagination = ({
    pageGroupLength,
    pageIndex,
    take,
    takeOptions = [10, 20, 30],
    totalLength,
    setTake,
    setPageIndex,
    prevPageHandler,
    nextPageHandler,
    prevPageGroupHandler,
    nextPageGroupHandler,
    resetPaginationHandler,
    disabled,
    padding = '40px 0'
}: PaginationProps) => {
    // 페이지네이션 셀렉트박스 show, hide
    const [isOpen, setIsOpen] = useState(false);
    const outRef = useRef<HTMLUListElement>(null);

    const lengthStartNum = pageGroupLength + pageIndex + 1;
    const lengthEndNum =
        totalLength < pageGroupLength + pageIndex + take
            ? totalLength
            : pageGroupLength + pageIndex + take;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            // 해당 이벤트가 영역 밖이라면
            if (outRef.current && !outRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [setIsOpen]);

    return (
        <Wrapper $padding={padding}>
            {/* 총 수량 보이는 영역 */}
            <Container>
                <Text $margin="0 8px 0 0">{`${lengthStartNum}-${lengthEndNum} of ${
                    totalLength ? totalLength : 0
                }`}</Text>
                <Image src={StackIcon} />
                <TakeSelect
                    name="pageAmount"
                    value={take}
                    onChange={(e) => {
                        setTake(Number(e.target.value));
                        resetPaginationHandler();
                    }}
                >
                    {[...takeOptions].map((option, index) => (
                        <Option key={`${index}-${option}`} value={option}>
                            {option}
                        </Option>
                    ))}
                </TakeSelect>
            </Container>

            {/* 이동 버튼 영역 */}
            <Container $gap={4}>
                <Button
                    onClick={prevPageGroupHandler}
                    disabled={disabled.prevPageGroup}
                >
                    <HiChevronDoubleLeft
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
                <Button onClick={prevPageHandler} disabled={disabled.prevPage}>
                    <HiChevronLeft
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
                <PageSelect onClick={() => setIsOpen(!isOpen)}>
                    {(pageGroupLength + pageIndex + take) / take}
                </PageSelect>
                <Button onClick={nextPageHandler} disabled={disabled.nextPage}>
                    <HiChevronRight
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
                <Button
                    onClick={nextPageGroupHandler}
                    disabled={disabled.nextPageGroup}
                >
                    <HiChevronDoubleRight
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
                {/* 페이지네이션 셀렉트박스 */}
                {isOpen && (
                    <ListContainer ref={outRef}>
                        {pageGroupLength !== 0 ? (
                            <List key="prev" onClick={prevPageGroupHandler}>
                                이전
                            </List>
                        ) : (
                            <></>
                        )}
                        {[...Array(10)].map((_, i) => (
                            <Fragment key={pageGroupLength / take + 1 + i}>
                                {pageGroupLength / take + 1 + i <=
                                    Math.ceil(totalLength / take) && (
                                    <List
                                        onClick={() => {
                                            setPageIndex(i * take);
                                            setIsOpen(!isOpen);
                                        }}
                                    >
                                        {pageGroupLength / take + 1 + i}
                                    </List>
                                )}
                            </Fragment>
                        ))}
                        {totalLength - take * 10 <= pageGroupLength ? (
                            <></>
                        ) : (
                            <List key="next" onClick={nextPageGroupHandler}>
                                다음
                            </List>
                        )}
                    </ListContainer>
                )}
            </Container>
        </Wrapper>
    );
};

export default Pagination;

const Wrapper = styled.div<{ $padding?: string }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ $padding }) => $padding};
    width: 100%;
`;
const Container = styled.div<{ $gap?: number }>`
    position: relative;
    display: flex;
    align-items: center;
    font-family: PretendardRegular, sans-serif;

    ${({ $gap }) =>
        $gap &&
        css`
            gap: ${$gap}px;
        `}
`;
const Image = styled.img`
    display: block;
`;
const Text = styled.span<{ $margin?: string; $fontColor?: string }>`
    display: inline-block;
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
    color: ${({ $fontColor }) =>
        $fontColor ? $fontColor : theme.colors.blackColor};
`;
const TakeSelect = styled.select`
    margin-left: 8px;
    padding: 0 8px;
    width: 48px;
    height: 24px;
    border: ${theme.colors.ultraLightGrayBorder} solid 1px;
    border-radius: 4px;
    background: url(${ArrowDown}) no-repeat right 8px top 50%;
    background-size: 8px;
    font-size: 15px;
    font-family: PretendardRegular, sans-serif;
`;
const Option = styled.option``;
const Button = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    border: ${theme.colors.ultraLightGrayBorder} solid 1px;
    background-color: ${theme.colors.whiteColor};
    border-radius: 4px;
    &:hover {
        background-color: ${darken(0.01, theme.colors.whiteColor)};
    }
    &:active {
        background-color: ${darken(0.02, theme.colors.whiteColor)};
    }
    &:disabled {
        background-color: ${darken(0.03, theme.colors.whiteColor)};
        cursor: auto;
    }
`;
const PageSelect = styled.span`
    /* margin: 0 4px; */
    padding: 0 8px;
    width: 48px;
    height: 24px;
    border: ${theme.colors.ultraLightGrayBorder} solid 1px;
    border-radius: 4px;
    background: url(${ArrowDown}) no-repeat right 8px top 50%;
    background-size: 8px;
    cursor: pointer;
`;
const ListContainer = styled.ul`
    position: absolute;
    bottom: 0;
    right: 56px;
    z-index: 10;
    width: 48px;
    border: ${theme.colors.ultraLightGrayBorder} solid 1px;
    border-radius: 4px;
    background-color: ${theme.colors.whiteColor};
`;
const List = styled.li`
    padding: 4px 10px;
    width: 100%;
    background-color: ${theme.colors.whiteColor};
    cursor: pointer;
    &:hover {
        background-color: ${darken(0.02, theme.colors.whiteColor)};
    }
    &:active {
        background-color: ${darken(0.02, theme.colors.whiteColor)};
    }
`;
